import React, { createContext, useContext, useMemo, useState } from 'react'
import type { LyricPayload } from '../../app/protocol'
import { buildJizuraTimeline } from './timeline.js'

const STORAGE_KEY = 'musey-jizura-settings'
export const JIZURA_INITIAL_SEED = 20260922
export const JIZURA_STYLES = [
  ['noir', '黑白色差'], ['crimson', '绯红信号'], ['caution', '警示黄'],
  ['magenta', '流行粉'], ['paper', '纸与墨'], ['hud', '数据界面'],
  ['mint', '薄荷'], ['specimen', '字样标本'], ['transit', '轨道交通'],
  ['blueprint', '蓝图'], ['rouge', '胭脂'], ['mono', '单色'],
  ['sakura', '樱花'], ['ocean', '深海'], ['sunset', '落日'], ['forest', '森林手帖'],
  ['vapor', '蒸汽波'], ['newsprint', '报纸'], ['synth80', '80 年代合成器'],
  ['kraft', '牛皮纸'], ['candy', '糖果'], ['acid', '酸性霓虹'], ['sumi', '墨与朱'], ['gold', '金夜'],
] as const

type Settings = { style: string; seed: number; motion: number; glitch: number; extra: boolean }
const defaults: Settings = { style: 'noir', seed: JIZURA_INITIAL_SEED, motion: 0.3, glitch: 0.1, extra: false }

function readSettings(): Settings {
  try {
    const stored = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '{}')
    const amount = (value: unknown, fallback: number) => (
      typeof value === 'number' && Number.isFinite(value) ? Math.min(1, Math.max(0, value)) : fallback
    )
    return {
      style: JIZURA_STYLES.some(([id]) => id === stored?.style) ? stored.style : defaults.style,
      seed: Number.isSafeInteger(stored?.seed) ? stored.seed : defaults.seed,
      motion: amount(stored?.motion, defaults.motion),
      glitch: amount(stored?.glitch, defaults.glitch),
      extra: stored?.extra === true,
    }
  } catch {
    return { ...defaults }
  }
}

type ContextValue = {
  settings: Settings
  update: (patch: Partial<Settings>) => void
  timeline: ReturnType<typeof buildJizuraTimeline>
  recordingBusy: boolean
}
const JizuraContext = createContext<ContextValue | null>(null)

export function JizuraProvider({ children, lyricPayload, duration, recordingBusy }: {
  children: React.ReactNode
  lyricPayload: LyricPayload
  duration: number
  recordingBusy: boolean
}) {
  const [settings, setSettings] = useState(readSettings)
  const timeline = useMemo(() => buildJizuraTimeline(lyricPayload, duration), [lyricPayload, duration])
  const update = (patch: Partial<Settings>) => {
    if (recordingBusy) return
    setSettings((previous) => {
      const next = { ...previous, ...patch }
      try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next)) } catch { /* optional storage */ }
      return next
    })
  }
  return (
    <JizuraContext.Provider value={{ settings, update, timeline, recordingBusy }}>
      {children}
    </JizuraContext.Provider>
  )
}

export function useJizura() {
  const value = useContext(JizuraContext)
  if (!value) throw new Error('JIZURA requires the Musey stage session provider')
  return value
}

export function JizuraSettingsPanel({ accentColor }: { accentColor?: string }) {
  const { settings, update, recordingBusy } = useJizura()
  return (
    <fieldset className="jizura-settings" disabled={recordingBusy}>
      <label className="jizura-settings-switch">
        <span>使用扩展演出</span>
        <input type="checkbox" checked={settings.extra}
          onChange={(event) => update({ extra: event.target.checked })} />
        <span className="jizura-switch-track" aria-hidden="true">
          <span className="jizura-switch-thumb" />
        </span>
      </label>
      {(['motion', 'glitch'] as const).map((key) => (
        <label key={key}>
          {key === 'motion' ? '动作强度' : '故障效果'} · {Math.round(settings[key] * 100)}%
          <input type="range" min="0" max="1" step="0.05" value={settings[key]}
            style={{ accentColor }}
            onChange={(event) => update({ [key]: Number(event.target.value) })} />
        </label>
      ))}
    </fieldset>
  )
}
